import { useMemo } from 'react';
import { getMarkdown, parseMarkdownToStructure } from 'stream-markdown-parser';
import styles from './StreamingMarkdown.module.css';

interface ParsedNode {
  type: string;
  content?: string;
  children?: ParsedNode[];
  level?: number;
  ordered?: boolean;
  href?: string;
  title?: string;
  text?: string;
  language?: string;
  code?: string;
  items?: ParsedNode[];
  checked?: boolean;
}

interface StreamingMarkdownProps {
  content: string;
  className?: string;
  /** Larger type scale + pillar hierarchy for goal / document panels (vs chat). */
  variant?: 'default' | 'goalDocument';
}

const renderInlineContent = (children: ParsedNode[] | undefined, keyPrefix: string): React.ReactNode[] => {
  if (!children) return [];
  
  return children.map((child, index) => {
    const key = `${keyPrefix}-${index}`;
    
    switch (child.type) {
      case 'text':
        return <span key={key}>{child.content}</span>;
      
      case 'strong':
        return (
          <strong key={key} className={styles.strong}>
            {renderInlineContent(child.children, key)}
          </strong>
        );
      
      case 'em':
        return (
          <em key={key} className={styles.em}>
            {renderInlineContent(child.children, key)}
          </em>
        );
      
      case 'code_inline':
        return (
          <code key={key} className={styles.inlineCode}>
            {child.content}
          </code>
        );
      
      case 'link':
        return (
          <a 
            key={key} 
            href={child.href} 
            title={child.title}
            className={styles.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            {child.text || renderInlineContent(child.children, key)}
          </a>
        );
      
      case 'softbreak':
        return ' ';
      
      case 'hardbreak':
        return <br key={key} />;
      
      default:
        if (child.content) {
          return <span key={key}>{child.content}</span>;
        }
        if (child.children) {
          return <span key={key}>{renderInlineContent(child.children, key)}</span>;
        }
        return null;
    }
  });
};

const renderNode = (node: ParsedNode, index: number, keyPrefix: string): React.ReactNode => {
  const key = `${keyPrefix}-${index}`;
  
  switch (node.type) {
    case 'heading': {
      const level = node.level || 1;
      const Tag = `h${level}` as keyof JSX.IntrinsicElements;
      const headingClass = styles[`h${level}` as keyof typeof styles] || styles.h1;
      return (
        <Tag key={key} className={headingClass}>
          {renderInlineContent(node.children, key)}
        </Tag>
      );
    }
    
    case 'paragraph':
      return (
        <p key={key} className={styles.paragraph}>
          {renderInlineContent(node.children, key)}
        </p>
      );
    
    case 'list': {
      const items = node.items || node.children || [];
      if (node.ordered) {
        return (
          <ol key={key} className={styles.orderedList}>
            {items.map((item, i) => renderNode(item, i, `${key}-item`))}
          </ol>
        );
      }
      return (
        <ul key={key} className={styles.unorderedList}>
          {items.map((item, i) => renderNode(item, i, `${key}-item`))}
        </ul>
      );
    }
    
    case 'list_item':
      return (
        <li key={key} className={styles.listItem}>
          {node.children?.map((child, i) => {
            if (child.type === 'paragraph') {
              return renderInlineContent(child.children, `${key}-p-${i}`);
            }
            return renderNode(child, i, `${key}-child`);
          })}
        </li>
      );
    
    case 'code_block':
      return (
        <pre key={key} className={styles.codeBlock}>
          <code className={node.language ? styles[`lang-${node.language}`] : undefined}>
            {node.code || node.content}
          </code>
        </pre>
      );
    
    case 'blockquote':
      return (
        <blockquote key={key} className={styles.blockquote}>
          {node.children?.map((child, i) => renderNode(child, i, `${key}-bq`))}
        </blockquote>
      );
    
    case 'hr':
      return <hr key={key} className={styles.hr} />;
    
    case 'text':
      return <span key={key}>{node.content}</span>;
    
    default:
      if (node.children) {
        return (
          <div key={key}>
            {node.children.map((child, i) => renderNode(child, i, `${key}-child`))}
          </div>
        );
      }
      if (node.content) {
        return <span key={key}>{node.content}</span>;
      }
      return null;
  }
};

const normalizeContent = (text: string): string => {
  let normalized = text;
  
  if ((normalized.startsWith('"') && normalized.endsWith('"')) ||
      (normalized.startsWith("'") && normalized.endsWith("'"))) {
    normalized = normalized.slice(1, -1);
  }
  
  return normalized
    .replace(/\\n/g, '\n')
    .replace(/\\t/g, '\t')
    .replace(/\\r/g, '\r')
    .replace(/\\"/g, '"');
};

export const StreamingMarkdown = ({ content, className, variant = 'default' }: StreamingMarkdownProps) => {
  const nodes = useMemo(() => {
    if (!content) return [];
    try {
      const normalizedContent = normalizeContent(content);
      const md = getMarkdown();
      return parseMarkdownToStructure(normalizedContent, md) as ParsedNode[];
    } catch {
      return [];
    }
  }, [content]);

  if (nodes.length === 0 && content) {
    return <span className={className}>{content}</span>;
  }

  return (
    <div
      className={`${styles.container} ${className || ''} ${
        variant === 'goalDocument' ? styles.goalDocumentVariant : ''
      }`}
    >
      {nodes.map((node, index) => renderNode(node, index, 'root'))}
    </div>
  );
};
